// client/src/utils/offlineWrite.js
// One entry point for writes that must survive a bad connection.
//
// The subtle part is the idempotency key. It is minted *before* the online
// attempt and reused verbatim if that attempt fails and the write gets queued.
// That covers the case the whole design exists for: the request reached the
// server and committed, but the response was lost on the way back. The client
// cannot distinguish that from a genuine failure, so it queues and retries — and
// because the retry carries the same key, the server replays the original
// response instead of applying the write a second time. Minting a fresh key at
// queue time would silently reintroduce exactly the duplicate we're preventing.

import { fetchJson } from './fetchJson';
import { queueWrite } from './writeOutbox';
import { newIdempotencyKey, captureScope } from './outboxIdentity';

// True when the request never got a response — offline, DNS failure, dropped
// connection. An HTTP error response means the server was reached, which is a
// real rejection and must surface to the user rather than being queued.
export function isOfflineError(err) {
  if (!err) return false;
  if (err.response) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const msg = String(err.message || '').toLowerCase();
  return msg.includes('network request failed') || msg.includes('network error') || msg.includes('failed to fetch');
}

/**
 * Send a write, falling back to the offline queue if the network is unavailable.
 *
 * @returns {Promise<{queued: boolean, result?: any}>} `queued: true` means it was
 *          stored locally and will be delivered on reconnect — tell the user that
 *          rather than reporting success outright.
 * @throws  the original error for genuine server rejections (validation, permission,
 *          closed evaluation window), which queueing would only hide.
 */
export async function sendOrQueue({ kind, endpoint, method = 'POST', body, headers = {}, label }) {
  const idempotencyKey = newIdempotencyKey();
  const scope = captureScope();

  try {
    const result = await fetchJson(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
        ...headers,
      },
      body: JSON.stringify(body ?? {}),
    });
    return { queued: false, result };
  } catch (err) {
    if (!isOfflineError(err)) throw err;

    await queueWrite({ kind, endpoint, method, body, label, idempotencyKey, scope });
    return { queued: true };
  }
}
